import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useAccessToken } from "src/api/useAccessToken";
import { useSnackBar } from "src/components/Reusable/CustomSnackbarProvider";
import { useRealmApp } from "src/store/RealmApp";

export const accountSettings = {
  internalProjectUpdateNotifications: () =>
    ["settings", "account", "notifications", "internalProjectUpdates"] as const,
};

type DayTime = {
  isEnabled: boolean;
  time: string;
  timezone: string;
};

export type InternalProjectUpdateAccountNotificationSettingsData = {
  days: {
    monday: DayTime;
    tuesday: DayTime;
    wednesday: DayTime;
    thursday: DayTime;
    friday: DayTime;
    saturday: DayTime;
    sunday: DayTime;
  };
};

export const useGetInternalUpdateRemindersForUser = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    accountSettings.internalProjectUpdateNotifications(),
    async (): Promise<InternalProjectUpdateAccountNotificationSettingsData> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/internalUpdateRemindersForUser`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useUpdateInternalUpdateRemindersForUser = () => {
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();

  return useMutation({
    mutationFn: async (notificationSettings: {
      days?: {
        monday: DayTime;
        tuesday: DayTime;
        wednesday: DayTime;
        thursday: DayTime;
        friday: DayTime;
        saturday: DayTime;
        sunday: DayTime;
      };
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateInternalUpdateReminderForUser`,
        notificationSettings,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating notification settings", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(
        accountSettings.internalProjectUpdateNotifications()
      );
    },
  });
};
